import type { Idl, Program } from '@coral-xyz/anchor';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import type { Connection, TransactionInstruction } from '@solana/web3.js';
import { type PublicKey, SystemProgram } from '@solana/web3.js';
import type BN from 'bn.js';
import { deriveEventAuthorityPda, deriveGiftAdminPda, deriveGiftPda } from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, invokeMethod } from './common.js';

// ─── Legacy HandleVault builders (stubs — the corresponding contract
//     instructions were removed). Kept as no-op throwers so any
//     un-migrated caller fails loudly instead of silently constructing
//     a malformed transaction. Removed once Phase 6 finishes the
//     frontend hook swap.

export interface CreateHandleVaultParams extends BuildParams {
  payer: PublicKey;
  platform: string;
  handle: string;
  cycleIndex?: number;
}
export interface InitNextVaultCycleParams extends BuildParams {
  payer: PublicKey;
  handleHash: Uint8Array;
  currentCycleIndex: number | undefined;
}
export interface SendUsdcToHandleParams extends BuildParams {
  sender: PublicKey;
  handleHash: Uint8Array;
  cycleIndex: number;
  amount: BN;
  message: string;
  anonymous: boolean;
  xIdStr: BN;
  sequenceNumber: number;
  feeDestinationAta: PublicKey;
  relayPayer?: PublicKey;
}
export interface SendOutcomeTokenToHandleParams extends SendUsdcToHandleParams {
  maxFeeUsdc: BN;
  outcome: PublicKey;
  ammPool: PublicKey;
  outcomeMint: PublicKey;
  senderOutcomeAta: PublicKey;
  handleVaultOutcomeAta: PublicKey;
}
export interface WithdrawGiftDepositParams extends BuildParams {
  sender: PublicKey;
  handleHash: Uint8Array;
  cycleIndex: number;
  fundingWallet: PublicKey;
  sequenceNumber: number;
  vaultAssetAta: PublicKey;
  fundingWalletAssetAta: PublicKey;
  assetMint: PublicKey;
}
export interface ClaimHandleVaultVersionedTxParams extends BuildParams {
  claimant: PublicKey;
  destinationWallet: PublicKey;
  relayPayer: PublicKey;
  handleHash: Uint8Array;
  cycleIndex: number;
  nonce: Uint8Array;
  expiryUnix: number;
  expectedDepositCount: number;
  escrowAuthorityPublicKey: Uint8Array;
  escrowAuthoritySignature: Uint8Array;
  altAddress: PublicKey;
  // ALLOW-UNKNOWN: Solana AddressLookupTableAccount carries dynamic data; consumed by `TransactionMessage.compileToV0Message`.
  altAccount: unknown;
  recentBlockhash: string;
  // ALLOW-UNKNOWN: Anchor accepts a generic AccountMeta[]-shaped list; only required by the caller path that re-derives them.
  remainingAccounts: unknown;
}

// ALLOW-UNKNOWN: Sentinel for IDL-disabled builders — params are validated by the typed wrapper that calls this.
function unavailableBuilder(name: string, connection: Connection, params: unknown): never {
  void connection;
  void params;
  throw new Error(`${name} is not available in the current markets IDL`);
}

export async function buildCreateHandleVault(
  connection: Connection,
  params: CreateHandleVaultParams,
): Promise<BuildResult> {
  return unavailableBuilder('create_handle_vault', connection, params);
}
export async function buildInitNextVaultCycle(
  connection: Connection,
  params: InitNextVaultCycleParams,
): Promise<BuildResult> {
  return unavailableBuilder('init_next_vault_cycle', connection, params);
}
export async function buildSendUsdcToHandle(
  connection: Connection,
  params: SendUsdcToHandleParams,
): Promise<BuildResult> {
  return unavailableBuilder('send_usdc_to_handle', connection, params);
}
export async function buildSendOutcomeTokenToHandle(
  connection: Connection,
  params: SendOutcomeTokenToHandleParams,
): Promise<BuildResult> {
  return unavailableBuilder('send_outcome_token_to_handle', connection, params);
}
export async function buildWithdrawGiftDeposit(
  connection: Connection,
  params: WithdrawGiftDepositParams,
): Promise<BuildResult> {
  return unavailableBuilder('withdraw_gift_deposit', connection, params);
}
export async function buildClaimHandleVaultVersionedTx(
  connection: Connection,
  params: ClaimHandleVaultVersionedTxParams,
): Promise<BuildResult> {
  return unavailableBuilder('claim_handle_vault', connection, params);
}

// ─── New gift builders — the live contract surface ──────────────────

/** Mirror of the on-chain `GiftKind` Anchor enum tag mapping. */
export type GiftKindVariant = 'bondingCurveToken' | 'prizePoolShare' | 'usdc';

function giftKindToAnchorVariant(kind: GiftKindVariant): Record<string, Record<string, never>> {
  // Anchor enums serialise as `{ variantName: {} }` in JS.
  return { [kind]: {} };
}

export interface CreateGiftParams extends BuildParams {
  /** GiftConfig PDA (looked up server-side from deployer+label). */
  giftConfig: PublicKey;
  /** Off-chain handler hash — SHA-256 of the canonical handler string. */
  handlerHash: Uint8Array;
  /** Gift kind (drives downstream indexer settlement). */
  kind: GiftKindVariant;
  /** Token amount in mint base units. D6 for USDC, raw for outcome tokens. */
  amount: BN;
  /** Mint of the gifted token. */
  mint: PublicKey;
  /** Backend authority pubkey (signer on the ix; pays init rent). */
  backendAuthority: PublicKey;
}

/**
 * Build the `create_gift` instruction. Backend-only — only
 * `backend_authority` is required as signer; the source treasury
 * funding is done out-of-band via plain SPL transfer to the GiftAdmin
 * ATA before / after this instruction.
 */
export async function buildCreateGift(
  connection: Connection,
  params: CreateGiftParams,
  loadProgram: (c: Connection) => Promise<Program<Idl>>,
): Promise<BuildResult> {
  if (params.handlerHash.length !== 32) {
    throw new Error('buildCreateGift: handler_hash must be 32 bytes');
  }
  const program = await loadProgram(connection);
  const [giftPda] = deriveGiftPda(params.giftConfig, params.mint, params.handlerHash);
  const [eventAuthority] = deriveEventAuthorityPda(program.programId);

  // Anchor's typed method invocation. Args order matches the IDL:
  // `create_gift(handler_hash: [u8;32], kind: GiftKind, amount: u64)`.
  const ix: TransactionInstruction = await invokeMethod(
    program,
    'createGift',
    Array.from(params.handlerHash),
    giftKindToAnchorVariant(params.kind),
    params.amount,
  )
    .accounts({
      backendAuthority: params.backendAuthority,
      giftConfig: params.giftConfig,
      mint: params.mint,
      gift: giftPda,
      systemProgram: SystemProgram.programId,
      eventAuthority,
      program: program.programId,
    })
    .instruction();

  return createBuildResult([ix], [params.backendAuthority], 80_000);
}

export interface ClaimGiftParams extends BuildParams {
  giftConfig: PublicKey;
  handlerHash: Uint8Array;
  mint: PublicKey;
  /** Recipient Turnkey-controlled wallet — signer on the ix. */
  recipient: PublicKey;
  /** Backend authority — also a signer. Pays the recipient ATA init rent. */
  backendAuthority: PublicKey;
}

/**
 * Build the `claim_gift` instruction. DUAL-SIGNER: both
 * `backend_authority` and `recipient` are required signers. The
 * recipient ATA is initialised via `init_if_needed` (rent paid by
 * backend); the source treasury → recipient ATA transfer is signed by
 * the GiftAdmin PDA via the markets program's `invoke_signed` path.
 */
export async function buildClaimGift(
  connection: Connection,
  params: ClaimGiftParams,
  loadProgram: (c: Connection) => Promise<Program<Idl>>,
): Promise<BuildResult> {
  if (params.handlerHash.length !== 32) {
    throw new Error('buildClaimGift: handler_hash must be 32 bytes');
  }
  const program = await loadProgram(connection);
  const [giftPda] = deriveGiftPda(params.giftConfig, params.mint, params.handlerHash);
  const [giftAdmin] = deriveGiftAdminPda(params.giftConfig);
  const sourceTreasury = getAssociatedTokenAddressSync(
    params.mint,
    giftAdmin,
    /*allowOwnerOffCurve=*/ true,
    TOKEN_PROGRAM_ID,
  );
  const recipientTokenAccount = getAssociatedTokenAddressSync(
    params.mint,
    params.recipient,
    /*allowOwnerOffCurve=*/ false,
    TOKEN_PROGRAM_ID,
  );
  const [eventAuthority] = deriveEventAuthorityPda(program.programId);

  const ix: TransactionInstruction = await invokeMethod(
    program,
    'claimGift',
    Array.from(params.handlerHash),
  )
    .accounts({
      backendAuthority: params.backendAuthority,
      giftConfig: params.giftConfig,
      gift: giftPda,
      mint: params.mint,
      giftAdmin,
      sourceTreasury,
      recipient: params.recipient,
      recipientTokenAccount,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
      eventAuthority,
      program: program.programId,
    })
    .instruction();

  return createBuildResult([ix], [params.backendAuthority, params.recipient], 120_000);
}
